import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import { useLocation } from 'react-router-dom';

import {Alert} from '@ui';

import popupEmitter from './PopupMessageService';

const PopupMessageContext = createContext();

let popupMessageId = 0;

export const PopupMessageProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [closingMessages, setClosingMessages] = useState(new Set());

    const location = useLocation();
    const prevLocationRef = useRef(null);
    const timeoutsRef = useRef(new Set());
    const isMountedRef = useRef(true);

    const maxMessages = 10;

    const areRoutesRelated = (currentPath, previousPath) => {
        if (!currentPath || !previousPath) return false;
        const getCurrentBasePath = (path) => {
            const [base] = path.split('/').filter(Boolean);

            return base ? `/${base}` : path;
        };

        return getCurrentBasePath(currentPath) === getCurrentBasePath(previousPath);
    };

    useEffect(() => {
        const currentPath = location.pathname;
        const previousPath = prevLocationRef.current;

        if (null !== previousPath && currentPath !== previousPath && !areRoutesRelated(currentPath, previousPath)) {
            setMessages([]);
            setClosingMessages(new Set());
        }
        prevLocationRef.current = currentPath;
    }, [location.pathname]);

    useEffect(() => {
        isMountedRef.current = true;

        const handlePopupMessage = ({ message, severity, autoHideDuration, stayVisible, errorDetailsMessage }) => {
            if (!isMountedRef.current) return;
            const newMessage = {
                id: popupMessageId++,
                message,
                severity,
                autoHideDuration: stayVisible ? null : autoHideDuration,
                stayVisible,
                errorDetailsMessage,
                expanded: false,
            };
            setMessages(prev => {
                const updated = [...prev, newMessage];

                return updated.length > maxMessages ? updated.slice(-maxMessages) : updated;
            });
        };

        popupEmitter.on('popupMessage', handlePopupMessage);

        return () => {
            isMountedRef.current = false;
            popupEmitter.off('popupMessage', handlePopupMessage);
            timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            timeoutsRef.current.clear();
        };
    }, []);

    const hidePopupMessage = (id) => {
        if (!isMountedRef.current) return;
        setClosingMessages(prev => new Set([...prev, id]));
        const hideTimeout = setTimeout(() => {
            if (!isMountedRef.current) return;
            setMessages(prev => prev.filter(msg => msg.id !== id));
            setClosingMessages(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);

                return newSet;
            });
            timeoutsRef.current.delete(hideTimeout);
        }, 300);
        timeoutsRef.current.add(hideTimeout);
    };

    const expandMessage = (id) => {
        if (!isMountedRef.current) return;
        setMessages(prev =>
            prev.map(m =>
                m.id === id
                    ? { ...m, expanded: true, autoHideDuration: null }
                    : m
            )
        );
    };

    return (
        <PopupMessageContext.Provider value={{
            messageCount: messages.length,
            visibleCount: messages.length,
        }}>
            {children}
            <div className='popupMessage-container'>
                {messages.map((msg, index) => (
                    <Snackbar
                        key={msg.id}
                        open={!closingMessages.has(msg.id)}
                        autoHideDuration={msg.autoHideDuration}
                        onClose={(event, reason) => {
                            if ('timeout' === reason) {
                                hidePopupMessage(msg.id);
                            }
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        className={`popupMessage position-top-${index}`}
                        slots={{
                            transition: Slide,
                        }}
                        slotProps={{
                            transition: {
                                direction: 'left',
                                timeout: 300,
                            },
                        }}
                    >
                        <Alert
                            variant='filled'
                            onClose={() => hidePopupMessage(msg.id)}
                            severity={msg.severity}
                        >
                            <div>
                                <div>{msg.message}</div>
                                {msg.errorDetailsMessage && msg.expanded && (
                                    <div className='popup-details-text'>
                                        {msg.errorDetailsMessage}
                                    </div>
                                )}
                                {msg.errorDetailsMessage && !msg.expanded && (
                                    <div
                                        className='popup-details-link'
                                        onClick={() => expandMessage(msg.id)}
                                    >
                                        Show more details
                                    </div>
                                )}
                            </div>
                        </Alert>
                    </Snackbar>
                ))}
            </div>
        </PopupMessageContext.Provider>
    );
};

PopupMessageProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useSnackbar = () => useContext(PopupMessageContext);
